// Patient-side correction-request submission dialog. Identity fields on
// PatientInfo (name / dateOfBirth / gender / mobile / email) are clinic-owned
// and not directly editable from the portal; this dialog lets a patient
// file a request that the support / clinic team reviews and applies.
//
// Backend: in.aumhum.aumhum.patient.controller.MeCorrectionRequestsController.

import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import SessionDialog from '../../sessions/components/SessionDialog'
import { submitCorrectionRequest } from '../../../services/correctionRequestsService'

const Field = styled.label`
  display: block;
  font-size: 13px;
  color: ${(p) => p.theme?.color?.inkSoft || '#4B5563'};
  font-weight: 600;
  margin-top: 12px;
  margin-bottom: 6px;
`

const TextInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  font-family: inherit;
  font-size: 14px;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  &:focus {
    outline: 2px solid ${(p) => p.theme?.color?.primary || '#1F3A5F'};
    outline-offset: 1px;
  }
`

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  font-family: inherit;
  font-size: 14px;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
`

const TextArea = styled.textarea`
  width: 100%;
  min-height: 84px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  &:focus {
    outline: 2px solid ${(p) => p.theme?.color?.primary || '#1F3A5F'};
    outline-offset: 1px;
  }
`

const Current = styled.div`
  font-size: 13px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  color: ${(p) => p.theme?.color?.inkSoft || '#4B5563'};
`

const FIELD_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'dateOfBirth', label: 'Date of birth' },
  { value: 'gender', label: 'Gender' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'email', label: 'Email' },
  { value: 'other', label: 'Something else (free text)' },
]

const GENDER_OPTIONS = [
  { value: '', label: 'Select…' },
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not', label: 'Prefer not to say' },
]

/**
 * Snapshot of the current value for the selected field. Used to remind the
 * patient what's on file and to give the reviewer a baseline. The dialog
 * pulls these from the active profile passed in via {@code profile}.
 */
function currentFor(profile, fieldName) {
  if (!profile) return ''
  switch (fieldName) {
    case 'name':         return profile.displayName || ''
    case 'dateOfBirth':  return profile.dob || ''
    case 'gender':       return profile.gender || ''
    case 'mobile':       return profile.mobile || ''
    case 'email':        return profile.email || ''
    default:             return ''
  }
}

export default function CorrectionRequestDialog({ open, profile, onClose, onSubmitted }) {
  const [fieldName, setFieldName] = useState('name')
  const [requestedValue, setRequestedValue] = useState('')
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) {
      setFieldName('name')
      setRequestedValue('')
      setReason('')
      setError(null)
      setSubmitting(false)
    }
  }, [open])

  const isOther = fieldName === 'other'
  const currentValue = currentFor(profile, fieldName)
  const canSubmit = isOther
    ? reason.trim().length > 0
    : requestedValue.trim().length > 0

  async function handleSubmit() {
    if (!profile?.id) return
    setSubmitting(true)
    setError(null)
    try {
      await submitCorrectionRequest({
        patientId: profile.id,
        fieldName,
        requestedValue: isOther ? null : requestedValue.trim(),
        reason: reason.trim() || null,
      })
      if (typeof onSubmitted === 'function') onSubmitted()
      onClose()
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not submit. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <SessionDialog
      open={open}
      title="Request a correction"
      subtitle="Identity fields are owned by your clinic. We'll send this to the support / clinic team and notify you once they review."
      primaryLabel="Submit request"
      onPrimary={handleSubmit}
      primaryDisabled={!canSubmit}
      primaryLoading={submitting}
      onClose={onClose}
    >
      <Field htmlFor="cor-field">Field to correct</Field>
      <Select
        id="cor-field"
        value={fieldName}
        onChange={(e) => setFieldName(e.target.value)}
      >
        {FIELD_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>

      {!isOther && (
        <>
          <Field>Currently on file</Field>
          <Current>{currentValue || <em>not set</em>}</Current>
        </>
      )}

      {!isOther && (
        <>
          <Field htmlFor="cor-new">What should it be?</Field>
          {fieldName === 'gender' ? (
            <Select
              id="cor-new"
              value={requestedValue}
              onChange={(e) => setRequestedValue(e.target.value)}
            >
              {GENDER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          ) : fieldName === 'dateOfBirth' ? (
            <TextInput
              id="cor-new"
              type="date"
              value={requestedValue}
              onChange={(e) => setRequestedValue(e.target.value)}
              placeholder="yyyy-mm-dd"
            />
          ) : (
            <TextInput
              id="cor-new"
              type={fieldName === 'email' ? 'email' : fieldName === 'mobile' ? 'tel' : 'text'}
              value={requestedValue}
              onChange={(e) => setRequestedValue(e.target.value)}
              placeholder={
                fieldName === 'name'
                  ? 'e.g. Aviral Pandey'
                  : fieldName === 'mobile'
                  ? '+91-9...'
                  : fieldName === 'email'
                  ? 'name@example.com'
                  : ''
              }
            />
          )}
        </>
      )}

      <Field htmlFor="cor-reason">{isOther ? 'What needs correcting?' : 'Reason (optional)'}</Field>
      <TextArea
        id="cor-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={
          isOther
            ? 'Describe what needs fixing — the support team will read this directly.'
            : 'Optional context for the reviewer.'
        }
      />

      {error ? (
        <p style={{ marginTop: 12, color: '#C24D49', fontSize: 13 }}>{error}</p>
      ) : null}
    </SessionDialog>
  )
}
